import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * v3.1 §5 Bausteine — Wartebildschirm primitive.
 *
 * Verbatim class strings (line 216):
 *   Kopfzeile `text-base` zentriert, `mb-4`
 *   Balken `h-2 w-full max-w-xs overflow-hidden rounded-full
 *           bg-dream-navy/15` mit Füllung `h-full bg-dream-gold
 *           transition-[width] ease-linear`
 *   Prozentzeile `mt-2 text-sm font-medium`
 *
 * "Übernommen werden Balken und Prozentzeile. Neu sind Kopfzeile und
 *  Kinderfrage-Karte. Kein Video, für den Buchfluss existiert keins."
 * (v3.1 §5 line 216)
 *
 * The Kinderfrage-Karte is out of scope here — its content pipeline
 * is Phase-6+ territory.
 *
 * ═══ WHERE THIS IS USED ═══════════════════════════════════════════════
 *
 * v3.1 §6 Schritt 2 line 287 — `anchor_pending` waits with
 * "Wir zeichnen dein Titelbild."
 * v3.1 §6 Schritt 4 line 352 — `preview_rendering` waits with
 * "Wir malen die ersten Seiten." + page-count position.
 * PWA additionally dispatches `light_gate_pending` to this primitive
 * with the anchor heading — see BookRoute + tracker
 * `[book-wait-light-gate-heading]`.
 *
 * ═══ BERUHIGUNGSZEILE ════════════════════════════════════════════════
 *
 * v3.1 §6 line 274: "Beruhigungszeile steht nur hier und in den
 *  Wartemomenten, nicht auf Entscheidungsbildschirmen." This
 * primitive optionally renders it below the teaser lead — Schritt 1
 * plus every Wartemoment. Not rendered on `preview_rendering` today
 * (PreviewWaitScreen does not pass it); revisit if v3.1 tightens.
 */

export interface WaitScreenProgress {
  /** 0-100 percent complete. Clamped by the primitive. */
  readonly percent: number;
  /** Label rendered below the bar (e.g. "Seite 2 von 4"). */
  readonly positionLabel: string;
}

export interface WaitScreenProps {
  readonly heading: string;
  readonly progress?: WaitScreenProgress;
  readonly teaserLead?: string;
  readonly beruhigungszeile?: string;
  /**
   * Sebastian ruling 2026-09-08 — optional slow-copy line that
   * reveals after `slowThresholdMs` on the same screen instance.
   * Wired only on the story-generation branches (`story_generating`
   * + `anchor_approved`) with 240000 ms per Sebastian's measured
   * durations (anchor image 1:25-2:30, story 0:45-1:58; 4 minutes
   * reads as "definitely longer than usual"). NOT wired on
   * `anchor_pending` / `light_gate_pending` — those are different
   * waits with different pacing. When both props are undefined the
   * primitive renders as it did pre-2026-09-08 (no timer, no line).
   */
  readonly slowCopy?: string;
  readonly slowThresholdMs?: number;
  /**
   * Block 4 · 4.1 (Sebastian ruling 2026-09-09) — optional body line
   * that renders under the heading with the same centred rhythm.
   * Wired on the initial anchor-render wait per Sebastian's Figuren
   * copy; other consumers pass undefined and get the pre-Block-4
   * layout.
   */
  readonly body?: string;
  /**
   * Block 4 · 4.1 (Sebastian ruling 2026-09-09) — optional spinner
   * rendered BELOW the copy. Wired on the initial anchor-render
   * wait ("with a spinner below" per spec). Off by default so
   * pre-Block-4 consumers keep the text-only layout.
   */
  readonly showSpinner?: boolean;
  /**
   * Block 3 correction (Sebastian test-book finding 2026-09-09) —
   * opt-in vertical centering for loader waits. Sets a min-height
   * plus `justify-center` so the small heading+body+spinner block
   * sits in the middle of the surrounding BookLane card instead of
   * hugging the top of a nearly-empty white area. Wired on
   * `light_gate_pending` (very brief wait, pure loader shape); other
   * WaitScreen consumers with progress bars leave this off.
   */
  readonly centered?: boolean;
}

export function WaitScreen({
  heading,
  progress,
  teaserLead,
  beruhigungszeile,
  slowCopy,
  slowThresholdMs,
  body,
  showSpinner,
  centered,
}: WaitScreenProps) {
  const clampedPercent =
    progress === undefined ? undefined : Math.round(Math.min(100, Math.max(0, progress.percent)));

  // Slow-copy reveal timer. Fires exactly once per screen instance
  // (navigating away + back resets the timer, which is fine — a
  // customer who left and came back is a fresh wait). No timer runs
  // when the props are absent, so the pre-2026-09-08 render path is
  // a strict subset.
  const [showSlow, setShowSlow] = useState(false);
  useEffect(() => {
    if (slowCopy === undefined || slowThresholdMs === undefined) return;
    const id = setTimeout(() => setShowSlow(true), slowThresholdMs);
    return () => clearTimeout(id);
  }, [slowCopy, slowThresholdMs]);

  const outerClass = centered
    ? 'flex min-h-[24rem] flex-col items-center justify-center pt-6 pb-4'
    : 'flex flex-col items-center pt-6 pb-4';
  return (
    <div className={outerClass}>
      <h1 className="mb-4 text-center text-base font-semibold text-dream-navy">{heading}</h1>

      {body !== undefined && (
        <p className="mx-auto mb-6 max-w-[520px] text-center text-base text-dream-navy">{body}</p>
      )}

      {progress !== undefined && (
        <>
          <div
            className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-dream-navy/15"
            role="progressbar"
            aria-valuenow={clampedPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={heading}
          >
            <div
              className="h-full bg-dream-gold transition-[width] duration-200 ease-linear"
              style={{ width: `${clampedPercent}%` }}
            />
          </div>
          <p className="mt-2 text-sm font-medium text-dream-navy">{progress.positionLabel}</p>
        </>
      )}

      {teaserLead !== undefined && (
        <p className="mt-8 text-center text-base text-dream-navy">{teaserLead}</p>
      )}

      {/*
        Block 4 review fix (Sebastian ruling 2026-09-09) — spinner
        sits IMMEDIATELY BELOW the main copy (heading + body +
        progress / teaserLead) so the connection between the "Das
        dauert ein bis drei Minuten" body and the visible motion is
        not broken by asides. slowCopy stays close to the spinner as
        its "we're taking longer than usual" status update.
        Beruhigungszeile drops to the bottom as the aside it is.
      */}
      {showSpinner === true && (
        <Loader2 className="mt-6 h-8 w-8 animate-spin text-dream-navy/70" aria-hidden />
      )}

      {showSlow && slowCopy !== undefined && (
        <p role="status" aria-live="polite" className="mt-6 text-center text-sm text-dream-navy">
          {slowCopy}
        </p>
      )}

      {beruhigungszeile !== undefined && (
        <p className="mt-6 text-center text-xs text-dream-navy/70">{beruhigungszeile}</p>
      )}
    </div>
  );
}
